import React from "react";
import { Link, useNavigate } from "react-router-dom";

/**
 * Sanitizes any mention of PropertySpy or its domain variants to Alpine Straits Partners.
 */
export function sanitizeWriteupText(text) {
  if (!text || typeof text !== "string") return "";
  return text
    .replace(/\bproperty\s*spy['’]s\b/gi, "Alpine Straits Partners'")
    .replace(/\bproperty\s*spy\b/gi, "Alpine Straits Partners")
    .replace(/\bpropertyspy\b/gi, "Alpine Straits Partners")
    .replace(/https?:\/\/(?:www\.)?propertyspy\.(?:com|sg|io|co|net)/gi, "https://www.alpinestraitspartners.com.sg")
    .replace(/\b(?:www\.)?propertyspy\.(?:com|sg|io|co|net)\b/gi, "alpinestraitspartners.com.sg");
}

/**
 * Strips all markdown and HTML tags for compact preview cards.
 */
export function stripMarkdownAndSanitize(text) {
  if (!text || typeof text !== "string") return "";
  let s = sanitizeWriteupText(text);
  // HTML links <a ...>text</a> -> text
  s = s.replace(/<a\b[^>]*>(.*?)<\/a>/gi, "$1");
  // Markdown links [text](url) -> text
  s = s.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
  // Bold/italic **text** -> text
  s = s.replace(/\*\*([^*]+)\*\*/g, "$1");
  s = s.replace(/\*([^*]+)\*/g, "$1");
  s = s.replace(/__([^_]+)__/g, "$1");
  s = s.replace(/_([^_]+)_/g, "$1");
  // Clean multiple newlines and spaces
  return s.replace(/\s+/g, " ").trim();
}

/**
 * Parse an inline string into React nodes:
 * Supports markdown links [text](url), HTML links <a href="url">text</a>, bold **text**, italic *text*, bare urls.
 */
function parseInline(rawText, keyPrefix = "inline") {
  if (!rawText) return [];

  // Matcher for markdown link, html link, bold, italic, or raw URL
  const tokenRegex = /(\[([^\]]+)\]\(([^)]+)\)|<a\s+(?:[^>]*?\s+)?href=["']([^"']+)["'][^>]*>(.*?)<\/a>|\*\*([^*]+)\*\*|\*([^*]+)\*|(https?:\/\/[^\s<]+))/g;

  const result = [];
  let lastIndex = 0;
  let match;
  let index = 0;

  while ((match = tokenRegex.exec(rawText)) !== null) {
    // Push preceding plain text
    if (match.index > lastIndex) {
      result.push(rawText.substring(lastIndex, match.index));
    }

    const key = `${keyPrefix}-${index++}`;

    if (match[2] && match[3]) {
      // Markdown link: [text](url)
      const linkText = match[2];
      const linkUrl = match[3];
      result.push(<InteractiveLink key={key} href={linkUrl}>{linkText}</InteractiveLink>);
    } else if (match[4] && match[5]) {
      // HTML link: <a href="url">text</a>
      const linkUrl = match[4];
      const linkText = match[5];
      result.push(<InteractiveLink key={key} href={linkUrl}>{linkText}</InteractiveLink>);
    } else if (match[6]) {
      // Bold: **text**
      result.push(
        <strong key={key} className="font-semibold text-[#0A1128]">
          {match[6]}
        </strong>
      );
    } else if (match[7]) {
      // Italic: *text*
      result.push(
        <em key={key} className="italic text-slate-800">
          {match[7]}
        </em>
      );
    } else if (match[8]) {
      // Bare URL: https://...
      const url = match[8];
      result.push(<InteractiveLink key={key} href={url}>{url}</InteractiveLink>);
    }

    lastIndex = tokenRegex.lastIndex;
  }

  // Push remaining text
  if (lastIndex < rawText.length) {
    result.push(rawText.substring(lastIndex));
  }

  return result;
}

function InteractiveLink({ href, children }) {
  const rawHref = (href || "").trim();

  // Check if link is an internal anchor or route
  const isInternal =
    rawHref.startsWith("/") ||
    rawHref.startsWith("#") ||
    rawHref.includes("alpinestraitspartners.com.sg") ||
    rawHref.startsWith("localhost");

  let path = rawHref;
  if (path.includes("alpinestraitspartners.com.sg")) {
    try {
      const parsed = new URL(path);
      path = parsed.pathname + parsed.search + parsed.hash;
    } catch {
      // fallback to original
    }
  }

  const linkStyle =
    "text-[#0A1128] font-medium underline underline-offset-4 decoration-[#C5A880] hover:text-[#C5A880] transition-colors cursor-pointer";

  if (isInternal) {
    const handleClick = (e) => {
      // Handle hash scroll if linking to landing section
      if (path.includes("#")) {
        const parts = path.split("#");
        const targetPath = parts[0] || "/";
        const hashId = parts[1];

        if (window.location.pathname === targetPath || (targetPath === "/" && window.location.pathname === "")) {
          e.preventDefault();
          const targetEl = document.getElementById(hashId);
          if (targetEl) {
            targetEl.scrollIntoView({ behavior: "smooth" });
            window.history.pushState(null, "", `#${hashId}`);
            return;
          }
        }
      }
    };

    return (
      <Link to={path} onClick={handleClick} className={linkStyle}>
        {children}
      </Link>
    );
  }

  // External link
  return (
    <a
      href={rawHref}
      target="_blank"
      rel="noopener noreferrer"
      className={linkStyle}
    >
      {children}
    </a>
  );
}

export default function RichWriteup({ text, className = "" }) {
  if (!text) return null;

  // 1. Sanitize all PropertySpy mentions
  const sanitized = sanitizeWriteupText(text).replace(/\r\n/g, "\n");

  // 2. Split into paragraphs
  const paragraphs = sanitized.split(/\n\s*\n/);

  return (
    <div className={`space-y-4 text-sm text-slate-700 leading-relaxed ${className}`}>
      {paragraphs.map((pText, pIdx) => {
        const trimmed = pText.trim();
        if (!trimmed) return null;

        // Handle single newlines inside paragraph with linebreaks
        const lines = trimmed.split("\n");

        return (
          <p key={`p-${pIdx}`}>
            {lines.map((line, lIdx) => (
              <React.Fragment key={`line-${pIdx}-${lIdx}`}>
                {lIdx > 0 && <br />}
                {parseInline(line, `p-${pIdx}-l-${lIdx}`)}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
